import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { guestGuard } from './core/guards/guest.guard';
import { permissionGuard } from './core/guards/permission.guard';

export const appRoutes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'login',
  },
  {
    path: 'login',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./features/auth/pages/login/login.component').then(
        (m) => m.LoginComponent
      ),
  },
  {
    // Deep-link del QR del equipo (§6.5.8/§6.5.12). Requiere sesión; el authGuard
    // guarda el returnUrl para retomar esta URL tras el login si no había sesión.
    path: 'maintain/scan',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/maintain-scan/maintain-scan.component').then(
        (m) => m.MaintainScanComponent
      ),
  },
  {
    path: 'home',
    canActivate: [authGuard],
    canActivateChild: [permissionGuard],
    loadComponent: () =>
      import('./layout/main-layout/main-layout.component').then(
        (m) => m.MainLayoutComponent
      ),
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'dashboard',
      },
      {
        // Dashboard sin guard de permiso: es el aterrizaje seguro / destino de redirección.
        path: 'dashboard',
        loadComponent: () =>
          import('./features/home/pages/dashboard/dashboard.component').then(
            (m) => m.DashboardComponent
          ),
      },

      {
        path: 'administracion/usuarios',
        data: { permission: 'gl.iam.user.read' },
        loadComponent: () =>
          import('./features/home/pages/admin-usuarios/admin-usuarios.component').then(
            (m) => m.AdminUsuariosComponent
          ),
      },
      {
        path: 'administracion/turnos',
        data: { permission: 'gl.iam.shift.read' },
        loadComponent: () =>
          import('./features/home/pages/admin-turnos/admin-turnos.component').then(
            (m) => m.AdminTurnosComponent
          ),
      },
      {
        path: 'administracion/areas-operacion',
        data: { permission: 'gl.iam.location.read' },
        loadComponent: () =>
          import('./features/home/pages/admin-areas-operacion/admin-areas-operacion.component').then(
            (m) => m.AdminAreasOperacionComponent
          ),
      },
      {
        path: 'administracion/empresa',
        data: { permission: 'gl.iam.tenant.read' },
        loadComponent: () =>
          import('./features/home/pages/admin-empresa/admin-empresa.component').then(
            (m) => m.AdminEmpresaComponent
          ),
      },
      {
        // Nueva sección: Empresa Servicio Técnico (proveedoras de técnicos).
        // Endpoint/menú/permiso aún pendientes en backend; la vista opera en
        // modo "no disponible por el momento". El permiso lo otorgará el backend.
        path: 'administracion/empresa-servicio-tecnico',
        data: { permission: 'gl.iam.service_tenant.read' },
        loadComponent: () =>
          import('./features/home/pages/admin-empresa-servicio-tecnico/admin-empresa-servicio-tecnico.component').then(
            (m) => m.AdminEmpresaServicioTecnicoComponent
          ),
      },

      {
        path: 'control-operativo/equipo',
        data: { permission: 'gl.forklift.forklift.read' },
        loadComponent: () =>
          import('./features/home/pages/control-equipo/control-equipo.component').then(
            (m) => m.ControlEquipoComponent
          ),
      },
      {
        path: 'control-operativo/dispositivo',
        data: { permission: 'gl.device.device.read' },
        loadComponent: () =>
          import('./features/home/pages/control-dispositivo/control-dispositivo.component').then(
            (m) => m.ControlDispositivoComponent
          ),
      },
      {
        path: 'control-operativo/mantenimiento',
        data: { permission: 'gl.forklift.maintenance_config.read' },
        loadComponent: () =>
          import('./features/home/pages/control-mantenimiento/control-mantenimiento.component').then(
            (m) => m.ControlMantenimientoComponent
          ),
      },
      {
        path: 'control-operativo/inspecciones',
        data: { permission: 'gl.forklift.inspection_config.read' },
        loadComponent: () =>
          import('./features/home/pages/control-inspecciones/control-inspecciones.component').then(
            (m) => m.ControlInspeccionesComponent
          ),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'grupo',
          },
          {
            path: 'grupo',
            loadComponent: () =>
              import('./features/home/pages/control-inspecciones-grupo/control-inspecciones-grupo.component').then(
                (m) => m.ControlInspeccionesGrupoComponent
              ),
          },
          {
            path: 'personalizadas',
            loadComponent: () =>
              import('./features/home/pages/control-inspecciones-personalizadas/control-inspecciones-personalizadas.component').then(
                (m) => m.ControlInspeccionesPersonalizadasComponent
              ),
          },
        ],
      },
      {
        path: 'control-operativo/calificaciones',
        data: { permission: 'gl.forklift.scoring.read' },
        loadComponent: () =>
          import('./features/home/pages/control-calificaciones/control-calificaciones.component').then(
            (m) => m.ControlCalificacionesComponent
          ),
      },
      {
        path: 'tiempo-real/operativo',
        data: { permission: 'gl.realtime.operations.read' },
        loadComponent: () =>
          import('./features/home/pages/tiempo-real-operativo/tiempo-real-operativo.component').then(
            (m) => m.TiempoRealOperativoComponent
          ),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'seguridad',
          },
          {
            // Seguridad y Desempeño unidos en una sola vista operativa.
            path: 'seguridad',
            loadComponent: () =>
              import('./features/home/pages/tiempo-real-operativo-seguridad/tiempo-real-operativo-seguridad.component').then(
                (m) => m.TiempoRealOperativoSeguridadComponent
              ),
          },
        ],
      },
      {
        path: 'tiempo-real/mantenimiento',
        data: { permission: 'gl.realtime.maintenance.read' },
        loadComponent: () =>
          import('./features/home/pages/tiempo-real-mantenimiento/tiempo-real-mantenimiento.component').then(
            (m) => m.TiempoRealMantenimientoComponent
          ),
      },
      {
        path: 'tiempo-real/productividad',
        data: { permission: 'gl.realtime.productivity.read' },
        loadComponent: () =>
          import('./features/home/pages/tiempo-real-productividad/tiempo-real-productividad.component').then(
            (m) => m.TiempoRealProductividadComponent
          ),
      },
      {
        path: 'tiempo-real/documentos',
        data: { permission: 'gl.realtime.documents.read' },
        loadComponent: () =>
          import('./features/home/pages/tiempo-real-documentos/tiempo-real-documentos.component').then(
            (m) => m.TiempoRealDocumentosComponent
          ),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'operadores',
          },
          {
            path: 'operadores',
            loadComponent: () =>
              import('./features/home/pages/tiempo-real-documentos-operadores/tiempo-real-documentos-operadores.component').then(
                (m) => m.TiempoRealDocumentosOperadoresComponent
              ),
          },
          {
            path: 'equipos',
            loadComponent: () =>
              import('./features/home/pages/tiempo-real-documentos-equipos/tiempo-real-documentos-equipos.component').then(
                (m) => m.TiempoRealDocumentosEquiposComponent
              ),
          },
        ],
      },
      {
        path: 'tiempo-real/calificacion',
        data: { permission: 'gl.realtime.scoring.read' },
        loadComponent: () =>
          import('./features/home/pages/tiempo-real-calificacion/tiempo-real-calificacion.component').then(
            (m) => m.TiempoRealCalificacionComponent
          ),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'operadores',
          },
          {
            path: 'operadores',
            loadComponent: () =>
              import('./features/home/pages/tiempo-real-calificacion-operadores/tiempo-real-calificacion-operadores.component').then(
                (m) => m.TiempoRealCalificacionOperadoresComponent
              ),
          },
          {
            path: 'equipos',
            loadComponent: () =>
              import('./features/home/pages/tiempo-real-calificacion-equipos/tiempo-real-calificacion-equipos.component').then(
                (m) => m.TiempoRealCalificacionEquiposComponent
              ),
          },
        ],
      },
      {
        path: 'tiempo-real/geoliftcheck',
        data: { permission: 'gl.realtime.geoliftcheck.read' },
        loadComponent: () =>
          import('./features/home/pages/tiempo-real-geoliftcheck/tiempo-real-geoliftcheck.component').then(
            (m) => m.TiempoRealGeoliftcheckComponent
          ),
      },
      {
        path: 'tiempo-real/geoliftcare',
        data: { permission: 'gl.realtime.geoliftcare.read' },
        loadComponent: () =>
          import('./features/home/pages/tiempo-real-geoliftcare/tiempo-real-geoliftcare.component').then(
            (m) => m.TiempoRealGeoliftcareComponent
          ),
      },
      {
        path: 'informes/operativos',
        data: { permission: 'gl.report.operations.read' },
        loadComponent: () =>
          import('./features/home/pages/informes-operativos/informes-operativos.component').then(
            (m) => m.InformesOperativosComponent
          ),
      },
      {
        path: 'informes/mantenimiento',
        data: { permission: 'gl.report.maintenance.read' },
        loadComponent: () =>
          import('./features/home/pages/informes-mantenimiento/informes-mantenimiento.component').then(
            (m) => m.InformesMantenimientoComponent
          ),
      },
      {
        path: 'informes/productividad',
        data: { permission: 'gl.report.productivity.read' },
        loadComponent: () =>
          import('./features/home/pages/informes-productividad/informes-productividad.component').then(
            (m) => m.InformesProductividadComponent
          ),
      },
      {
        path: 'informes/calificacion',
        data: { permission: 'gl.report.scoring.read' },
        loadComponent: () =>
          import('./features/home/pages/informes-calificacion/informes-calificacion.component').then(
            (m) => m.InformesCalificacionComponent
          ),
      },
      {
        path: 'informes/geoliftcheck',
        data: { permission: 'gl.report.geoliftcheck.read' },
        loadComponent: () =>
          import('./features/home/pages/informes-geoliftcheck/informes-geoliftcheck.component').then(
            (m) => m.InformesGeoliftcheckComponent
          ),
      },
      {
        path: 'informes/geoliftcare',
        data: { permission: 'gl.report.geoliftcare.read' },
        loadComponent: () =>
          import('./features/home/pages/informes-geoliftcare/informes-geoliftcare.component').then(
            (m) => m.InformesGeoliftcareComponent
          ),
      },
      {
        path: 'tendencias/control',
        data: { permission: 'gl.trend.control.read' },
        loadComponent: () =>
          import('./features/home/pages/tendencias-control/tendencias-control.component').then(
            (m) => m.TendenciasControlComponent
          ),
      },
      {
        path: 'tendencias/mantenimiento',
        data: { permission: 'gl.trend.maintenance.read' },
        loadComponent: () =>
          import('./features/home/pages/tendencias-mantenimiento/tendencias-mantenimiento.component').then(
            (m) => m.TendenciasMantenimientoComponent
          ),
      },
      {
        path: 'tendencias/productividad',
        data: { permission: 'gl.trend.productivity.read' },
        loadComponent: () =>
          import('./features/home/pages/tendencias-productividad/tendencias-productividad.component').then(
            (m) => m.TendenciasProductividadComponent
          ),
      },
      {
        path: 'tendencias/geoliftcheck',
        data: { permission: 'gl.trend.geoliftcheck.read' },
        loadComponent: () =>
          import('./features/home/pages/tendencias-geoliftcheck/tendencias-geoliftcheck.component').then(
            (m) => m.TendenciasGeoliftcheckComponent
          ),
      },
      {
        path: 'tendencias/geoliftcare',
        data: { permission: 'gl.trend.geoliftcare.read' },
        loadComponent: () =>
          import('./features/home/pages/tendencias-geoliftcare/tendencias-geoliftcare.component').then(
            (m) => m.TendenciasGeoliftcareComponent
          ),
      },

      {
        // Nueva sección (debajo de Tendencias): Geoliftcheck del OPERADOR.
        // Vista propia del operador (distinta de las de supervisión). El permiso
        // y el ítem de menú los otorgará el backend; datos en modo mock por ahora.
        path: 'geoliftcheck-operador',
        data: { permission: 'gl.geoliftcheck.operator.read' },
        loadComponent: () =>
          import('./features/home/pages/geoliftcheck-operador/geoliftcheck-operador.component').then(
            (m) => m.GeoliftcheckOperadorComponent
          ),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'tablero' },
          {
            path: 'tablero',
            loadComponent: () =>
              import('./features/home/pages/geoliftcheck-operador-tablero/geoliftcheck-operador-tablero.component').then(
                (m) => m.GeoliftcheckOperadorTableroComponent
              ),
          },
          {
            path: 'inspeccion',
            loadComponent: () =>
              import('./features/home/pages/geoliftcheck-operador-inspeccion/geoliftcheck-operador-inspeccion.component').then(
                (m) => m.GeoliftcheckOperadorInspeccionComponent
              ),
          },
          {
            path: 'inspeccion-personalizada',
            loadComponent: () =>
              import('./features/home/pages/geoliftcheck-operador-inspeccion-personalizada/geoliftcheck-operador-inspeccion-personalizada.component').then(
                (m) => m.GeoliftcheckOperadorInspeccionPersonalizadaComponent
              ),
          },
          {
            path: 'consultas',
            loadComponent: () =>
              import('./features/home/pages/geoliftcheck-operador-consultas/geoliftcheck-operador-consultas.component').then(
                (m) => m.GeoliftcheckOperadorConsultasComponent
              ),
          },
        ],
      },

      {
        // Nueva sección (debajo de Tendencias): Geoliftcare del TÉCNICO.
        // Vista propia del técnico. Permiso + menú los otorgará el backend;
        // datos en modo mock por ahora.
        path: 'geoliftcare-tecnico',
        data: { permission: 'gl.geoliftcare.technician.read' },
        loadComponent: () =>
          import('./features/home/pages/geoliftcare-tecnico/geoliftcare-tecnico.component').then(
            (m) => m.GeoliftcareTecnicoComponent
          ),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'pendiente' },
          {
            path: 'pendiente',
            loadComponent: () =>
              import('./features/home/pages/geoliftcare-tecnico-pendiente/geoliftcare-tecnico-pendiente.component').then(
                (m) => m.GeoliftcareTecnicoPendienteComponent
              ),
          },
          {
            path: 'completado',
            loadComponent: () =>
              import('./features/home/pages/geoliftcare-tecnico-completado/geoliftcare-tecnico-completado.component').then(
                (m) => m.GeoliftcareTecnicoCompletadoComponent
              ),
          },
          {
            path: 'perfil',
            loadComponent: () =>
              import('./features/home/pages/geoliftcare-tecnico-perfil/geoliftcare-tecnico-perfil.component').then(
                (m) => m.GeoliftcareTecnicoPerfilComponent
              ),
          },
          {
            path: 'nuevo',
            loadComponent: () =>
              import('./features/home/pages/geoliftcare-tecnico-nuevo/geoliftcare-tecnico-nuevo.component').then(
                (m) => m.GeoliftcareTecnicoNuevoComponent
              ),
          },
        ],
      },
    ],
  },
  {
    path: '**',
    loadComponent: () =>
      import('./features/not-found/pages/not-found/not-found.component').then(
        (m) => m.NotFoundComponent
      ),
  },
];
